/**
 * Description cleanup for vendor identity (PRD phase 3). Pure string
 * transforms, no model call: the same raw descriptor family
 * ("ACH DEBIT 0614 SQ *BLUE BOTTLE 4471 SEATTLE WA" vs
 * "SQ *BLUE BOTTLE #2210 POS PURCHASE 07/14") must clean to strings a
 * single substring rule can match, and `deriveCounterpartyKey` over the
 * cleaned string is what phase 4 rules and phase 7 vendor rollups group on.
 *
 * The transform is deliberately single-pass: toggles apply in the PRD's
 * listed order, then custom words, then custom regexes, then a final space
 * collapse (when that toggle is on) and an unconditional trim. A later
 * toggle can in principle unmask a token an earlier toggle would have
 * caught (for example numbers-in-words turning "IN2020" into a standalone
 * "IN" after the state-code pass already ran); we accept that instead of
 * looping to a fixed point, because a fixed-point loop over user-supplied
 * regexes has no obvious termination bound. In practice the pass is
 * idempotent over real statement descriptors, and the test corpus asserts
 * that.
 */

export type DescriptionCleanupToggles = {
  removeDates: boolean
  removeCurrencyValues: boolean
  removePhoneNumbers: boolean
  removeStateCodes: boolean
  removeHashNumbers: boolean
  replacePunctuationWithSpaces: boolean
  removeSpecialCharacters: boolean
  removeNumbersInWords: boolean
  collapseSpaces: boolean
  removeShortNumbers: boolean
}

export type DescriptionCleanupConfig = {
  toggles: DescriptionCleanupToggles
  /** Only used when removeShortNumbers is on. */
  shortNumberMaxDigits: number
  /** Whole words removed case-insensitively. */
  customWords: readonly string[]
  /** Applied after all toggles, each as a global case-insensitive removal. */
  customRegexes: readonly string[]
}

/**
 * Every toggle defaults on except short-number removal: standalone digit
 * runs are often the only thing distinguishing two accounts at the same
 * vendor, so stripping them is opt-in.
 */
export const defaultDescriptionCleanupConfig: DescriptionCleanupConfig = {
  toggles: {
    removeDates: true,
    removeCurrencyValues: true,
    removePhoneNumbers: true,
    removeStateCodes: true,
    removeHashNumbers: true,
    replacePunctuationWithSpaces: true,
    removeSpecialCharacters: true,
    removeNumbersInWords: true,
    collapseSpaces: true,
    removeShortNumbers: false,
  },
  shortNumberMaxDigits: 3,
  customWords: [],
  customRegexes: [],
}

/**
 * Save-time caps. The length cap bounds compile cost before the step-budget
 * and per-row-timeout guards in the recompute runner ever see the pattern;
 * the list caps bound total per-row work to a known constant. The transform
 * re-applies the list caps defensively in case a config predates them.
 */
export const cleanupRegexMaxLength = 200
export const cleanupCustomWordsMax = 100
export const cleanupCustomRegexesMax = 25

export type CleanupRegexValidation =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: string }

/**
 * Many toggles remove "standalone tokens": a match bounded by whitespace or
 * the string edges. Lookarounds keep the boundaries zero-width so two
 * adjacent removable tokens ("SEATTLE WA CA") both match in one pass, and
 * the surrounding whitespace survives for the collapse step to normalise.
 */
const tokenBounded = (body: string, flags = "g"): RegExp =>
  new RegExp(`(?<=^|\\s)(?:${body})(?=\\s|$)`, flags)

/**
 * Dates as standalone tokens: MM/DD, MM/DD/YY, MM/DD/YYYY, YYYY-MM-DD, and
 * MM-DD. Shapes only, no month/day range checks: descriptors never carry
 * out-of-range dates, and range checks would miss zero-padded oddities.
 * Bare MMDD runs ("0614") are deliberately not treated as dates; they are
 * indistinguishable from store numbers and belong to the short-number
 * toggle.
 */
const datePattern = tokenBounded(
  String.raw`\d{1,2}/\d{1,2}(?:/(?:\d{4}|\d{2}))?|\d{4}-\d{2}-\d{2}|\d{1,2}-\d{1,2}`
)

/**
 * Currency as standalone tokens: $-prefixed amounts (cents optional) and
 * bare amounts with an explicit decimal part ("1,234.56", "45.00"). Plain
 * integers are never eaten here; they belong to the hash, embedded-number,
 * and short-number toggles.
 */
const currencyPattern = tokenBounded(
  String.raw`\$\d[\d,]*(?:\.\d{1,2})?|\d[\d,]*\.\d{1,2}`
)

/**
 * US 10-digit phone shapes as standalone tokens: (555) 123-4567,
 * 555-123-4567, 555.123.4567, an optional 1- or 1. country prefix, and a
 * bare 10-digit run. Exactly ten digits for the bare form so longer
 * reference numbers survive.
 */
const phonePattern = tokenBounded(
  String.raw`\(\d{3}\)\s?\d{3}[-.]\d{4}|(?:1[-.])?\d{3}[-.]\d{3}[-.]\d{4}|\d{10}`
)

const usStateCodes = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC",
] as const

/**
 * State codes match only as standalone UPPERCASE tokens, never
 * case-insensitively: "or", "in", "me" are ordinary English words. The
 * residual risk is a genuinely uppercase "OR"/"IN"/"OH" that is not a
 * state, but bank descriptors are overwhelmingly all-caps with the state
 * trailing a city, so we accept that false positive rather than miss the
 * state on every all-caps descriptor.
 */
const statePattern = tokenBounded(usStateCodes.join("|"))

/**
 * `#` followed by an alphanumeric run, anywhere (not token-bounded):
 * "CHECK #1234" and the glued "REF#AB12" are the same store/reference
 * noise.
 */
const hashNumberPattern = /#[0-9A-Za-z]+/g

/**
 * The punctuation set becomes a space so glued vendor tokens split apart
 * ("AMAZON.COM*ORDER" must not fuse into "AMAZONCOMORDER"). Everything
 * non-alphanumeric that is not in this set is left for the
 * special-character toggle, which removes outright.
 */
const punctuationPattern = /[*,.;:/\\\-_()#!?'"&+]/g

/**
 * Special characters are removed outright, not spaced: by this point the
 * punctuation pass has already split token-joining characters, so what
 * remains ($ % @ ~ and the like) is decoration. Unicode letters and digits
 * are kept so accented vendor names ("CAFÉ") survive.
 */
const specialCharacterPattern = /[^\p{L}\p{N}\s]/gu

/**
 * Digit runs glued to letters inside a token ("CHECKCARD0614" to
 * "CHECKCARD", "4471BOTTLE" to "BOTTLE"). Standalone all-digit tokens are
 * left alone; those belong to the short-number toggle.
 */
const numbersInWordsPattern = /(?<=\p{L})\p{N}+|\p{N}+(?=\p{L})/gu

const whitespaceRunPattern = /\s+/g

/**
 * Standalone digit runs of at most maxDigits. A non-finite or sub-1 value
 * falls back to the default so a corrupt stored config degrades to the
 * documented behaviour instead of throwing inside RegExp.
 */
const shortNumberPattern = (maxDigits: number): RegExp => {
  const digits =
    Number.isInteger(maxDigits) && maxDigits >= 1
      ? maxDigits
      : defaultDescriptionCleanupConfig.shortNumberMaxDigits
  return tokenBounded(String.raw`\d{1,${digits}}`)
}

const escapeForRegex = (literal: string): string =>
  literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/** Named backreferences and \1-\9, skipping escaped backslashes. */
const backreferencePattern = /(?:^|[^\\])(?:\\\\)*\\(?:[1-9]|k<)/

/**
 * A group that contains a quantifier and is later closed and quantified:
 * the (a+)+ shape whose backtracking is exponential. Scanning for the
 * shape textually (rather than parsing the pattern) is deliberately
 * conservative: it also rejects bounded quantifiers like {3}, escaped
 * parens, and sibling-group shapes such as (a+)(b)+ that are actually
 * safe. Rejecting a safe pattern is the right failure mode for a
 * save-time gate; accepting an exponential one is not.
 */
const nestedQuantifierPattern = /\(.*[*+{].*\)[*+{]/

/**
 * Validate a custom regex on save: syntax, length cap, and constructs that
 * can backtrack catastrophically (nested quantifiers, backreferences).
 * Lookbehind is also rejected: nothing in a substring-removal use case
 * needs it, and keeping the accepted grammar small keeps the worst-case
 * cost of a saved pattern predictable. Reject-with-reason, never throw.
 */
export const validateCleanupRegex = (pattern: string): CleanupRegexValidation => {
  if (pattern.trim().length === 0) {
    return { ok: false, reason: "Pattern is empty." }
  }
  if (pattern.length > cleanupRegexMaxLength) {
    return {
      ok: false,
      reason: `Pattern is longer than ${cleanupRegexMaxLength} characters.`,
    }
  }
  try {
    new RegExp(pattern, "gi")
  } catch {
    return { ok: false, reason: "Pattern is not a valid regular expression." }
  }
  if (backreferencePattern.test(pattern)) {
    return { ok: false, reason: "Backreferences are not allowed." }
  }
  if (pattern.includes("(?<=") || pattern.includes("(?<!")) {
    return { ok: false, reason: "Lookbehind is not allowed." }
  }
  if (nestedQuantifierPattern.test(pattern)) {
    return {
      ok: false,
      reason: "Quantified groups that contain a quantifier are not allowed.",
    }
  }
  return { ok: true }
}

/**
 * The pure transform. Returns the cleaned string and never touches the
 * input or the config. Standalone-token removals replace with the empty
 * string (their surrounding whitespace survives for the collapse pass);
 * custom regexes replace with a single space instead, because an arbitrary
 * pattern can end mid-token and a bare removal would fuse its neighbours
 * into a false vendor name. Custom regexes are applied only if they pass
 * validateCleanupRegex; invalid ones are skipped defensively so one bad
 * saved pattern cannot take down a recompute.
 */
export const cleanDescription = (
  config: DescriptionCleanupConfig,
  description: string
): string => {
  const { toggles } = config
  let text = description

  if (toggles.removeDates) text = text.replace(datePattern, "")
  if (toggles.removeCurrencyValues) text = text.replace(currencyPattern, "")
  if (toggles.removePhoneNumbers) text = text.replace(phonePattern, "")
  if (toggles.removeStateCodes) text = text.replace(statePattern, "")
  if (toggles.removeHashNumbers) text = text.replace(hashNumberPattern, "")
  if (toggles.replacePunctuationWithSpaces) {
    text = text.replace(punctuationPattern, " ")
  }
  if (toggles.removeSpecialCharacters) {
    text = text.replace(specialCharacterPattern, "")
  }
  if (toggles.removeNumbersInWords) {
    text = text.replace(numbersInWordsPattern, "")
  }
  if (toggles.collapseSpaces) text = text.replace(whitespaceRunPattern, " ")
  if (toggles.removeShortNumbers) {
    text = text.replace(shortNumberPattern(config.shortNumberMaxDigits), "")
  }

  for (const word of config.customWords.slice(0, cleanupCustomWordsMax)) {
    const trimmed = word.trim()
    if (trimmed.length === 0) continue
    text = text.replace(tokenBounded(escapeForRegex(trimmed), "gi"), "")
  }

  for (const pattern of config.customRegexes.slice(0, cleanupCustomRegexesMax)) {
    if (!validateCleanupRegex(pattern).ok) continue
    text = text.replace(new RegExp(pattern, "gi"), " ")
  }

  // Custom removals can reintroduce runs, so collapse again when asked; the
  // trim is unconditional so a fully-noise descriptor cleans to "".
  if (toggles.collapseSpaces) text = text.replace(whitespaceRunPattern, " ")
  return text.trim()
}

/**
 * Cleaned description lowercased and space-normalised. This is the grouping
 * key for phase 4 rules and phase 7 vendor rollups, so it must be stable
 * regardless of whether the collapse toggle was on when the description was
 * cleaned.
 */
export const deriveCounterpartyKey = (cleanedDescription: string): string =>
  cleanedDescription.toLowerCase().replace(whitespaceRunPattern, " ").trim()
